<script setup lang="ts">
import {computed, ref} from 'vue';
import {verifyApi} from '@/features/auth-verify/api/verify';
import WebEidCeremony from '@/features/auth-ceremony/WebEidCeremony.vue';
import type {
    WebEidCeremonyApi,
    WebEidCeremonyHandle,
    WebEidCeremonySuccess
} from '@/features/auth-ceremony/types';
import type {VerifyCompleteResponseDto, VerifyTranslations} from '@/features/auth-verify/types';

defineOptions({name: 'VerifyWebEid'});

const props = defineProps<{
    translations: VerifyTranslations;
    purpose: string;
    resourceId: string;
}>();

const emit = defineEmits<{
    (e: 'verified', token: string | null): void;
    (e: 'cancel'): void;
}>();

const ceremonyRef = ref<WebEidCeremonyHandle | null>(null);

const t = (key: string, fallback = '') => props.translations[key] ?? fallback ?? key;

const apiConfig: WebEidCeremonyApi = {
    initiate: () => verifyApi.initiateWebEid().then(result => ({
        ok: result.ok,
        data: result.data?.base64EncodedNonce ? {nonce: result.data.base64EncodedNonce} : undefined,
        errorCode: result.errorCode,
        networkError: result.networkError
    })),
    complete: (authToken) => verifyApi.completeWebEid({
        algorithm: authToken.algorithm,
        format: authToken.format,
        signature: authToken.signature,
        unverifiedCertificate: authToken.unverifiedCertificate,
        purpose: props.purpose,
        resourceId: props.resourceId
    }).then(result => ({
        ok: result.ok,
        data: result.data ? {...result.data} as Record<string, unknown> : undefined,
        errorCode: result.errorCode,
        networkError: result.networkError
    }))
};

async function handleStart() {
    if (ceremonyRef.value?.isBusy()) return;
    await ceremonyRef.value?.start();
}

function onSuccess(payload: WebEidCeremonySuccess) {
    // Token may be null when the server gates on the refreshed JWT cookie instead of minting
    // a single-use verification token (e.g. delete-user-account).
    const token = (payload.completion as VerifyCompleteResponseDto | undefined)?.token ?? null;
    emit('verified', token);
}

const isBusy = computed(() => ceremonyRef.value?.isBusy() ?? false);
</script>

<template>
    <div class="verify-method">
        <WebEidCeremony
            ref="ceremonyRef"
            :translations="translations"
            :api-config="apiConfig"
            test-id-prefix="verify-web-eid"
            @success="onSuccess"
        />

        <div class="form-controls">
            <button class="js-save-button btn btn-cta" type="button" :disabled="isBusy" @click="handleStart">
                <span class="js-translatable-span" data-translation-key="btn-start">{{ t('btn-start') }}</span>
            </button>
            <button class="js-cancel-button btn" type="button" :disabled="isBusy" @click="emit('cancel')">
                <span class="js-translatable-span" data-translation-key="btn-cancel">{{ t('btn-cancel') }}</span>
            </button>
        </div>
    </div>
</template>

<style scoped>
.verify-method { display: flex; flex-direction: column; gap: 16px; }

.form-controls {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
}
</style>
